import { useEffect, useState, useMemo } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Chip, CircularProgress, Divider, Grid, Paper, Stack, Typography, Button, FormControl, InputLabel, Select, MenuItem, TextField, IconButton, Link, Tabs, Tab } from "@mui/material";
import Sidebar from "../components/Sidebar";
import { getTicket, updateTicket } from "../Services/ticketsService";
import { getStatuts } from "../Services/statutsService";
import { getCollaborateurs } from "../Services/collaborateursService";
import { getClients } from "../Services/clientsService";
import { getContacts } from "../Services/contactsService";
import { validateTransitionBeforeApply } from "../Services/statusService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/date";
import { PrioriteChip } from "../utils/ticketPriorite";
import { chipColorForStatut } from "../utils/ticketStatut";
import { uploadTicketAttachment, getTicketAttachments, deleteTicketAttachment } from "../Services/attachmentsService";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../Services/http";
import TicketChat from "../components/TicketChat";
import TicketModal from "../components/TicketModal";

function labelFromIri(iri) {
  if (!iri || typeof iri !== 'string') return '';
  const parts = iri.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isCollaborateur = user?.isCollaborateur();
  const isClientUser = user?.isClient?.() ?? false;
  // Sur la page liste, l'édition était conditionnée par `canChangeTicketStatus`.
  // On conserve la compatibilité, tout en permettant aussi `canEditTickets` si présent.
  const canModifyTicket =
    (user?.canEditTickets?.() ?? false) || (user?.canChangeTicketStatus?.() ?? false);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statuts, setStatuts] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [statutId, setStatutId] = useState("");
  const [collaborateurId, setCollaborateurId] = useState("");
  const [tpsResolution, setTpsResolution] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [names, setNames] = useState({ client: "", contact: "", collaborateur: "" });
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const hasModifierTab = canModifyTicket;

  const statutsMap = useMemo(() => statuts.reduce((acc, s) => ({ ...acc, [String(s.id)]: s.libelle }), {}), [statuts]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [st, data] = await Promise.all([getStatuts(), getTicket(id)]);
        let collabs = [];
        let cl = [];
        let ct = [];
        if (isCollaborateur) {
          [collabs, cl, ct] = await Promise.all([
            getCollaborateurs(),
            getClients(),
            getContacts(),
          ]);
        } else if (user?.isClient?.()) {
          collabs = await getCollaborateurs();
        }
        if (!mounted) return;
        setStatuts(st);
        setCollaborateurs(collabs);
        setClients(cl);
        setContacts(ct);
        setTicket(data);
        setStatutId(labelFromIri(data.statutId));
        setCollaborateurId(labelFromIri(data.collaborateurId));
        setTpsResolution(data.tpsResolution ?? "");
        setNote(data.note || "");
        const atts = await getTicketAttachments(id);
        if (!mounted) return;
        setAttachments(atts);
        // Resolve participant names
        const [clientName, contactName, collabName] = await Promise.all([
          resolveClientName(data.clientId),
          resolveContactName(data.contactId),
          resolveCollaborateurName(data.collaborateurId, collabs),
        ]);
        if (!mounted) return;
        setNames({ client: clientName, contact: contactName, collaborateur: collabName });
      } catch (e) {
        if (!mounted) return;
        setError("Impossible de charger le ticket");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id, isCollaborateur, user]);

  // Indices dynamiques des onglets (dépendent des permissions / rôles)
  let _idx = 0;
  const summaryIndex = _idx++;
  let modifierIndex = null;
  if (hasModifierTab) modifierIndex = _idx++;
  let actionsIndex = null;
  let notesIndex = null;
  if (isCollaborateur) {
    actionsIndex = _idx++;
    notesIndex = _idx++;
  }
  const attachmentsIndex = _idx++;
  const messagesIndex = _idx++;

  async function resolveClientName(iri) {
    try {
      if (!iri) return '';
      const res = await api.get(iri, { headers: { Accept: 'application/ld+json' } });
      return res.data?.nom || labelFromIri(iri);
    } catch { return labelFromIri(iri); }
  }

  async function resolveContactName(iri) {
    try {
      if (!iri) return '';
      const res = await api.get(iri, { headers: { Accept: 'application/ld+json' } });
      const d = res.data || {};
      const full = `${d.prenom || ''} ${d.nom || ''}`.trim();
      return full || labelFromIri(iri);
    } catch { return labelFromIri(iri); }
  }

  async function resolveCollaborateurName(iri, collaborateursList = []) {
    try {
      if (!iri) return '';
      const collabId = labelFromIri(iri);
      const fromList = collaborateursList.find((c) => String(c.id) === String(collabId));
      if (fromList) {
        const full = `${fromList.prenom || ''} ${fromList.nom || ''}`.trim();
        if (full) return full;
      }
      const res = await api.get(iri, { headers: { Accept: 'application/ld+json' } });
      const d = res.data || {};
      const full = `${d.prenom || ''} ${d.nom || ''}`.trim();
      return full || labelFromIri(iri);
    } catch { return labelFromIri(iri); }
  }

  const currentStatutLabel = statutsMap[String(statutId)] || '';
  const requireTps = currentStatutLabel && currentStatutLabel.toLowerCase() === 'terminée';

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const uploadFiles = async (ticketId, files) => {
    try {
      for (const f of files) {
        await uploadTicketAttachment(ticketId, f);
      }
      showToast("Pièces jointes envoyées", { severity: "success" });
    } catch (e) {
      showToast("Échec de l'upload des pièces jointes", { severity: "error" });
    }
  };

  const handleSaveModal = async (form) => {
    if (!canModifyTicket) return null;

    try {
      const updated = await updateTicket(id, form);
      setTicket(updated);

      // Mettre à jour l'état local pour que la page reflète les nouvelles infos
      setStatutId(labelFromIri(updated.statutId));
      setCollaborateurId(labelFromIri(updated.collaborateurId));
      setTpsResolution(updated.tpsResolution ?? "");
      setNote(updated.note || "");

      const [clientName, contactName, collabName] = await Promise.all([
        resolveClientName(updated.clientId),
        resolveContactName(updated.contactId),
        resolveCollaborateurName(updated.collaborateurId),
      ]);
      setNames({ client: clientName, contact: contactName, collaborateur: collabName });

      showToast("Ticket mis à jour", { severity: "success" });
      setTabValue(summaryIndex); // fermer l'onglet "Modifier" après save
      return updated;
    } catch (e) {
      showToast("Échec de la mise à jour du ticket", { severity: "error" });
      throw e;
    }
  };

  const handleSave = async () => {
    setFormError("");
    
    // Validation des prérequis
    if (requireTps && (tpsResolution === "" || Number.isNaN(Number(tpsResolution)))) {
      setFormError("Temps de résolution requis pour le statut Terminée.");
      return;
    }

    // Validation de la transition de statut
    if (statutId !== labelFromIri(ticket.statutId)) {
      try {
        const validation = await validateTransitionBeforeApply(id, statutId, collaborateurId || null, tpsResolution || null);
        if (!validation.valid) {
          setFormError(validation.errors.join(', '));
          return;
        }
      } catch (error) {
        setFormError("Erreur lors de la validation de la transition");
        return;
      }
    }

    try {
      setSaving(true);
      const payload = { statut: statutId };
      if (tpsResolution !== "") {
        payload.tpsResolution = Number(tpsResolution);
      }
      if (collaborateurId !== "") {
        payload.collaborateur = collaborateurId;
      }
      const updated = await updateTicket(id, payload);
      setTicket(updated);
      // Mettre à jour le collaborateurId si modifié
      if (collaborateurId !== "") {
        setCollaborateurId(collaborateurId);
        // Résoudre le nom du nouveau collaborateur
        const newCollabName = await resolveCollaborateurName(updated.collaborateurId);
        setNames(prev => ({ ...prev, collaborateur: newCollabName }));
      } else if (collaborateurId === "") {
        setCollaborateurId("");
        setNames(prev => ({ ...prev, collaborateur: "" }));
      }
      showToast("Ticket mis à jour", { severity: 'success' });
    } catch (e) {
      setFormError("Échec de la mise à jour du ticket");
      showToast("Échec de la mise à jour du ticket", { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (att) => {
    try {
      await deleteTicketAttachment(att.id);
      setAttachments(attachments.filter((a) => a.id !== att.id));
      showToast("Pièce jointe supprimée", { severity: 'success' });
    } catch (e) {
      showToast("Échec de la suppression de la pièce jointe", { severity: 'error' });
    }
  };

  const handleSaveNote = async () => {
    if (!isCollaborateur) return;
    try {
      setNoteSaving(true);
      const updated = await updateTicket(id, { note });
      setTicket(updated);
      showToast("Note sauvegardée", { severity: 'success' });
    } catch (e) {
      showToast("Échec de la sauvegarde de la note", { severity: 'error' });
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <Box sx={{ flex: 1, p: 3 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '60vh' }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : ticket ? (
          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="h6">Ticket {ticket.numeroTicket}</Typography>
                <Typography variant="body2" color="text.secondary">Créé le {formatDate(ticket.dateCreation)}</Typography>
              </Stack>
              <Typography variant="h4" sx={{ mb: 3 }}>{ticket.titre}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Chip
                  color={chipColorForStatut(statutsMap[String(labelFromIri(ticket.statutId))] || labelFromIri(ticket.statutId))}
                  label={`Statut: ${statutsMap[String(labelFromIri(ticket.statutId))] || labelFromIri(ticket.statutId)}`}
                />
                <PrioriteChip
                  priorite={ticket.priorite}
                  prefix="Priorité: "
                />
              </Stack>
            </Stack>
            <Paper sx={{ p: 0 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleChangeTab} 
                variant="scrollable" 
                scrollButtons 
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  sx: {
                    height: 3,
                    borderRadius: 0,
                    backgroundColor: 'primary.main',
                    left: 0,
                    right: 0,
                    width: '100% !important',
                    transform: 'none !important',
                    bottom: 0,
                    top: 'auto',
                    position: 'absolute',
                    transition: 'none',
                  }
                }}
                sx={{ px: 1, pt: 1 }}
              >
                <Tab 
                  label="Résumé" 
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                />
                {hasModifierTab && (
                  <Tab
                    label="Modifier"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 1,
                      mr: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '8px 8px 0 0',
                      },
                    }}
                  />
                )}
                {isCollaborateur && (
                  <Tab 
                    label="Actions"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 1,
                      mr: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '8px 8px 0 0',
                      },
                    }}
                  />
                )}
                {isCollaborateur && (
                  <Tab 
                    label="Notes"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 1,
                      mr: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '8px 8px 0 0',
                      },
                    }}
                  />
                )}
                <Tab 
                  label="Pièces jointes"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                />
                <Tab 
                  label="Messages"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                />
              </Tabs>
              <Divider />
              <Box sx={{ p: 2 }}>
                {/* Résumé */}
                {tabValue === summaryIndex && (
                  <Stack gap={2}>
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Numéro</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{ticket.numeroTicket}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Statut</Typography>
                          <Chip size="small" color={chipColorForStatut(statutsMap[String(labelFromIri(ticket.statutId))] || labelFromIri(ticket.statutId))} label={statutsMap[String(labelFromIri(ticket.statutId))] || labelFromIri(ticket.statutId)} sx={{ fontSize: '0.7rem' }} />
                        </Paper>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Client</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{names.client || labelFromIri(ticket.clientId)}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Contact</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{names.contact || labelFromIri(ticket.contactId)}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Collaborateur</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{names.collaborateur || labelFromIri(ticket.collaborateurId) || '—'}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Création</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{formatDate(ticket.dateCreation)}</Typography>
                        </Paper>
                        {ticket.dateModif && (
                          <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Dernière modification</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{formatDate(ticket.dateModif)}</Typography>
                          </Paper>
                        )}
                        {ticket.tpsResolution != null && (
                          <Paper variant="outlined" sx={{ flex: 1, p: 1.5, textAlign: 'center', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>Temps de résolution</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>{ticket.tpsResolution} min</Typography>
                          </Paper>
                        )}
                      </Box>
                    </Paper>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>Description</Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2" whiteSpace="pre-wrap">{ticket.description}</Typography>
                    </Paper>
                  </Stack>
                )}

                {/* Modifier ticket */}
                {hasModifierTab && tabValue === modifierIndex && (
                  <TicketModal
                    inline
                    onClose={() => setTabValue(summaryIndex)}
                    onSave={handleSaveModal}
                    onUploadFiles={uploadFiles}
                    initialData={
                      ticket
                        ? {
                            titre: ticket.titre || "",
                            description: ticket.description || "",
                            client: labelFromIri(ticket.clientId),
                            contact: labelFromIri(ticket.contactId),
                            collaborateur: labelFromIri(ticket.collaborateurId),
                            statut: labelFromIri(ticket.statutId),
                            priorite: ticket.priorite ?? 3,
                            tpsResolution: ticket.tpsResolution ?? "",
                            note: ticket.note || "",
                          }
                        : null
                    }
                    clients={clients}
                    contacts={contacts}
                    collaborateurs={collaborateurs}
                    statuts={statuts}
                  />
                )}

                {/* Actions collaborateur */}
                {isCollaborateur && tabValue === actionsIndex && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Actions collaborateur</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="statut-label">Statut</InputLabel>
                    <Select labelId="statut-label" label="Statut" value={statutId} onChange={(e) => setStatutId(e.target.value)}>
                      {statuts.map((s) => (
                        <MenuItem key={s.id} value={String(s.id)}>{s.libelle}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="collaborateur-label">Collaborateur</InputLabel>
                    <Select labelId="collaborateur-label" label="Collaborateur" value={collaborateurId} onChange={(e) => setCollaborateurId(e.target.value)}>
                      <MenuItem value="">Aucun collaborateur</MenuItem>
                      {collaborateurs.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>{`${c.prenom || ''} ${c.nom || ''}`.trim() || c.id}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {requireTps && (
                    <TextField
                      label="Temps de résolution (min)"
                      type="number"
                      value={tpsResolution}
                      onChange={(e) => setTpsResolution(e.target.value)}
                      sx={{ minWidth: 220 }}
                    />
                  )}
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    Enregistrer
                  </Button>
                </Stack>
                {formError && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>{formError}</Typography>
                )}
              </Paper>
            )}

                {/* Notes collaborateur */}
                {isCollaborateur && tabValue === notesIndex && (
            <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>Notes internes</Typography>
              <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <TextField
                        multiline
                        rows={6}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Ajoutez une note interne sur ce ticket..."
                        variant="outlined"
                        fullWidth
                      />
                      <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button 
                          variant="contained" 
                          onClick={handleSaveNote} 
                          disabled={noteSaving}
                        >
                          {noteSaving ? 'Sauvegarde...' : 'Sauvegarder la note'}
                        </Button>
                      </Stack>
                    </Stack>
                </Paper>
                )}

                {/* Pièces jointes */}
                {tabValue === attachmentsIndex && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Pièces jointes</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {attachments.length === 0 && <Typography variant="body2" color="text.secondary">Aucune pièce jointe.</Typography>}
                {attachments.map((att) => (
                  <Stack key={att.id} direction="row" alignItems="center" spacing={1}>
                    <Link href={att.url} target="_blank" rel="noopener">{att.originalName || att.fileName}</Link>
                    {isCollaborateur && (
                      <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Paper>
                )}

                {/* Messages */}
                {tabValue === messagesIndex && (
            <TicketChat ticketId={id} />
                )}
              </Box>
            </Paper>

            <Stack direction="row" gap={1}>
              <Button component={RouterLink} to="/tickets" variant="outlined">Retour</Button>
            </Stack>
          </Stack>
        ) : null}
      </Box>
    </div>
  );
} 